import { LOCALES } from "./locales";

// Import shared translations
import sharedEn from "./shared/en.json";
import sharedFr from "./shared/fr.json";
import sharedDe from "./shared/de.json";
import sharedEs from "./shared/es.json";
import sharedIt from "./shared/it.json";

// Import types and utils
import {
	FlatTranslations,
	Messages,
	NestedTranslations,
} from "./types/translations";
import { flattenTranslations, getNestedValue } from "./utils/nestedKeyUtils";

// Cache for loaded module translations
const moduleCache: Record<string, Record<string, NestedTranslations>> = {
	[LOCALES.ENGLISH]: {},
	[LOCALES.FRENCH]: {},
	[LOCALES.GERMAN]: {},
	[LOCALES.SPANISH]: {},
	[LOCALES.ITALIAN]: {},
};

// Cache for flattened translations (for backward compatibility)
const flattenedCache: Record<string, Record<string, FlatTranslations>> = {
	[LOCALES.ENGLISH]: {},
	[LOCALES.FRENCH]: {},
	[LOCALES.GERMAN]: {},
	[LOCALES.SPANISH]: {},
	[LOCALES.ITALIAN]: {},
};

// Shared translations mapping
const SHARED_TRANSLATIONS: Record<string, NestedTranslations> = {
	[LOCALES.ENGLISH]: sharedEn,
	[LOCALES.FRENCH]: sharedFr,
	[LOCALES.GERMAN]: sharedDe,
	[LOCALES.SPANISH]: sharedEs,
	[LOCALES.ITALIAN]: sharedIt,
};

// Deep-merge two nested translation objects. Values from `override` win over
// `base` at the leaf level. Non-object values in either side are preserved
// from the side that has them. Used to overlay an active-locale module on top
// of its English baseline so missing keys auto-fall-back to English.
function deepMergeTranslations(
	base: NestedTranslations,
	override: NestedTranslations
): NestedTranslations {
	const out: NestedTranslations = { ...base };
	for (const [k, v] of Object.entries(override)) {
		const baseVal = out[k];
		if (
			baseVal &&
			typeof baseVal === "object" &&
			!Array.isArray(baseVal) &&
			v &&
			typeof v === "object" &&
			!Array.isArray(v)
		) {
			out[k] = deepMergeTranslations(
				baseVal as NestedTranslations,
				v as NestedTranslations
			);
		} else {
			out[k] = v;
		}
	}
	return out;
}

// Function to merge translations with proper namespacing
const mergeTranslations = (
	shared: NestedTranslations,
	module: NestedTranslations,
	moduleName: string
): NestedTranslations => {
	const merged: NestedTranslations = { ...shared };

	// Add module translations with namespace
	merged[moduleName] = module;

	// Also create flattened version for backward compatibility
	flattenedCache[LOCALES.ENGLISH][moduleName] = flattenTranslations(
		module,
		moduleName
	);
	flattenedCache[LOCALES.FRENCH][moduleName] = flattenTranslations(
		module,
		moduleName
	);

	return merged;
};

// Function to load module translations with caching
export const loadModuleTranslations = async (
	moduleName: string,
	locale: string
): Promise<NestedTranslations> => {
	// Check cache first
	if (moduleCache[locale][moduleName]) {
		return moduleCache[locale][moduleName];
	}

	try {
		// Dynamically import module translations
		// Special handling for vehicleJourney in shared components
		// Special handling for testDriveLoanApplication nested under credit-line
		// Special handling for swan-auth nested under account
		// Special handling for onboarding/initial-form with nested path
		// Special handling for home module (physical location is dashboard)
		let moduleTranslations;
		if (moduleName === "vehicleJourney") {
			moduleTranslations = await import(
				`../app/shared/components/vehicle-journey/i18n/${locale}.json`
			);
		} else if (moduleName === "testDriveLoanApplication") {
			moduleTranslations = await import(
				`../app/credit-line/(pages)/test-drive-loan-application/i18n/${locale}.json`
			);
		} else if (moduleName === "swan-auth") {
			moduleTranslations = await import(
				`../app/account/swan-auth/i18n/${locale}.json`
			);
		} else if (moduleName === "onboarding/initial-form") {
			moduleTranslations = await import(
				`../app/onboarding/initial-form/i18n/${locale}.json`
			);
		} else if (moduleName === "onboarding/capability-discovery") {
			moduleTranslations = await import(
				`../app/onboarding/capability-discovery/i18n/${locale}.json`
			);
		} else if (moduleName === "home") {
			// Home module translations are now located in dashboard directory
			moduleTranslations = await import(`../app/dashboard/i18n/${locale}.json`);
		} else if (moduleName === "invoices") {
			// The pre-split /invoices page is gone; its shared code (and these
			// translations) moved under accounts/_shared. The key namespace stays
			// "invoices" so no call-site changes.
			moduleTranslations = await import(
				`../app/accounts/_shared/invoices/i18n/${locale}.json`
			);
		} else if (moduleName === "organization-appraisal-detail") {
			// Physically nested under the (pages)/disposition/[appraisalId] route
			// so its i18n directory sits next to the page that owns it, rather
			// than folded into the shared "organization" module's keys.
			moduleTranslations = await import(
				`../app/organization/(pages)/disposition/[appraisalId]/i18n/${locale}.json`
			);
		} else {
			moduleTranslations = await import(
				`../app/${moduleName}/i18n/${locale}.json`
			);
		}
		const translations = moduleTranslations.default as NestedTranslations;

		// Cache the translations
		moduleCache[locale][moduleName] = translations;

		// Create flattened version for backward compatibility
		flattenedCache[locale][moduleName] = flattenTranslations(
			translations,
			moduleName
		);

		return translations;
	} catch (error) {
		console.warn(
			`Failed to load translations for module ${moduleName} (${locale}):`,
			error
		);
		return {};
	}
};

// Every module whose translations are merged into the active locale. A module
// missing from this list renders its keys raw at runtime, so it is exported and
// resolved through ``loadModuleTranslations`` by
// ``__tests__/translation-modules-resolve.test.ts`` — except the modules that
// test allowlists as ``KNOWN_MISSING``, which have no i18n directory yet.
export const TRANSLATION_MODULES = [
	"listing",
	"appraisals",
	"deals",
	"documents",
	"carte-grise",
	"siv-archive",
	"invoices",
	"home",
	"leads",
	"calls",
	"user",
	"checkout",
	"payment-checkout",
	"login",
	"forgot-password",
	"my-cars",
	"workshop",
	"pricing",
	"credit-line",
	"credit-application-v2",
	"onboarding-documents",
	"account",
	"schedule",
	"shared",
	"loans",
	"registration-consent",
	"stripe-onboarding",
	"registration/invitation",
	"referral",

	"settings",
	"openbanking",
	"livre-de-police",
	"vehicleJourney",
	"testDriveLoanApplication",
	"swan-auth",
	"onboarding/initial-form",
	"onboarding/capability-discovery",
	"universal",
	"purchase-requests",
	"defacto-signature",
	"infinit-ai",
	"audits",
	"credit-application/showcase",
	"upgrade",
	"car-check",
	"business-showcase",
	"b2c-financing",
	"customer-financing",
	"stock-performance",
	"sales-performance",
	"dashboard-builder",
	"infinit-network",
	"referrals",
	"build-your-agent",
	"morning",
	"voice-assistant",
	"agent-runs",
	"sign-up",
	"photo-enhancer",
	"accounting",
	"accounts/sales-invoices",
	"accounts/purchase-invoices",
	"accounts/suppliers",
	"accounts/automation",
	"accounts/profit-loss",
	"accounts/balance-sheet",
	"accounts/_shared",
	"website",
	"blog-posts",
	"social-media",
	"support",
	"organization",
	"organization-appraisal-detail",
	"whatsapp-agents",
];

// Load all translations for a specific locale, with English fallback layered
// underneath so any key missing in the active locale renders the EN value
// (mirroring what the UI was rendering before the i18n refactor extracted
// hardcoded JSX text into keys).
export const loadTranslations = async (locale: string): Promise<Messages> => {
	const isEnglish = locale === LOCALES.ENGLISH;

	// Shared: EN as base, locale on top (or just locale if EN).
	const sharedActive: NestedTranslations =
		SHARED_TRANSLATIONS[locale] ?? SHARED_TRANSLATIONS[LOCALES.ENGLISH];
	const shared: NestedTranslations = isEnglish
		? sharedActive
		: deepMergeTranslations(SHARED_TRANSLATIONS[LOCALES.ENGLISH], sharedActive);

	// Start with shared translations as nested structure
	let messages: NestedTranslations = { ...shared };

	// Load module translations
	const modules = TRANSLATION_MODULES;

	// Load all modules in parallel. For non-EN locales also load the EN
	// version of each module concurrently (not sequentially) and overlay the
	// active-locale values on top — any key missing in the active locale
	// falls back to its EN value.
	const moduleResults = await Promise.all(
		modules.map(async moduleName => {
			if (isEnglish) {
				const activeTranslations = await loadModuleTranslations(
					moduleName,
					locale
				);
				return { moduleName, translations: activeTranslations };
			}
			const [activeTranslations, enTranslations] = await Promise.all([
				loadModuleTranslations(moduleName, locale),
				loadModuleTranslations(moduleName, LOCALES.ENGLISH),
			]);
			return {
				moduleName,
				translations: deepMergeTranslations(enTranslations, activeTranslations),
			};
		})
	);

	for (const { moduleName, translations } of moduleResults) {
		messages = mergeTranslations(messages, translations, moduleName);
	}

	// Create and cache flattened version of the entire translations object
	const flattenedMessages = flattenTranslations(messages);

	// Return combined object for both nested and flat access
	return {
		...flattenedMessages, // For backward compatibility
		_nested: messages, // For new nested key access
	};
};

// Function to get a translation with fallback
export const getTranslation = (
	messages: Messages,
	key: string,
	fallback?: string
): string => {
	// If messages has _nested property, use it for nested key access
	if ("_nested" in messages && typeof messages._nested === "object") {
		// First try direct lookup from flat keys (backward compatibility)
		if (key in messages && typeof messages[key] === "string") {
			return messages[key] as string;
		}

		// Then try nested lookup using dot notation
		const nestedValue = getNestedValue(messages._nested, key);
		if (nestedValue !== undefined) {
			return nestedValue;
		}
	} else {
		// Legacy flat access - try direct lookup
		const translation = messages[key];
		if (typeof translation === "string") {
			return translation;
		}
	}

	// If no translation found and no fallback provided, return the key
	if (!fallback) {
		console.warn(`Missing translation for key: ${key}`);
		return key;
	}

	return fallback;
};
